import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@core-ui/Components/ui/dialog';
import { Button } from '@core-ui/Components/ui/button';
import { Badge } from '@core-ui/Components/ui/badge';

import { Loader2 } from 'lucide-react';

import type { ContactListDto } from '@features/contacts';

interface DeleteListDialogProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  contactList: ContactListDto | null;
  isLoading: boolean;
  onConfirmDelete: () => void;
}

export default function DeleteListDialog ({
  isOpen,
  onOpenChange,
  contactList,
  isLoading,
  onConfirmDelete,
}: DeleteListDialogProps): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px] bg-card border-border text-card-foreground rounded-md p-4 md:p-6 shadow">
        <DialogHeader>
          <DialogTitle>{t('contacts.deleteListDialog.title')}</DialogTitle>
          <DialogDescription>
            {t('contacts.deleteListDialog.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-md border border-border p-3">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="truncate font-medium">{contactList?.title ?? '—'}</div>
            </div>
            <Badge variant="secondary" className="rounded-full text-xs">
              CSV
            </Badge>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            className="border border-border text-muted-foreground hover:bg-muted"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            {t('contacts.deleteListDialog.cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirmDelete}
            disabled={isLoading}
            className="gap-2"
          >
            {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('contacts.deleteListDialog.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
